import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import qs from 'qs';
import moment from 'moment';
import csv from 'csv-stringify';

import endpoints from '../../../config/endpoints';
import http from '../../shared/http';
import intl from '../../shared/intl';

import createCalendar from '../../shared/calendar';
import createColumnControl from '../components/column-control';
import createFilters from '../../shared/filters';
import createGroups from '../components/groups';
import createSaveExport from '../components/save-export';
import filtersInitialState from '../shared/filter-config';
import {actions as conversionsActions} from './../conversions';

import { tableInitialState, transformRows } from '../components/table/general';

import {
	getRequestQuery,
	mapQueryFilters,
	getCurrentCurrency,
	toggleInitialCurrency,
	initCurrencies,
	initDefaultCurrenciesAction
} from '../shared/utils';
import { REFETCH_ALL_DATA } from '../../root';

import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import find from 'lodash/find';
import _get from 'lodash/get';

const isRocket = process.env.CPA_ENV === 'rocket';

const calendar = createCalendar({
	namespace: 'statistics',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const columnControl = createColumnControl({
	namespace: 'statistics'
});

const filterInitialState = [].concat(filtersInitialState, {
	title: 'Сплит',
	suggest: [{
		placeholder: 'Выберите сплит-сессию',
		key: 'session_ids',
		options: {
			source: endpoints.STATISTICS.FILTERS.SPLIT_SESSIONS,
			type: 'fromServer'
		}
	}]
});

const filters = createFilters({
	namespace: 'statistics',
	stateNodeName: 'filters',
	filtersInitialState: filterInitialState
});

const groups = createGroups({
	namespace: 'statistics'
});

const saveExport = createSaveExport({
	namespace: 'statistics'
});

const FETCH_DATA_SUCCESS = 'statistics/fetch-data-success';
const FETCH_DATA_ERROR = 'statistics/fetch-data-error';

const SORT_BY_COLUMN = 'statistics/table-sort-by-column';
const SORT_BY_GROUP = 'statistics/table-sort-by-group';

const STATS_RESET = 'statistics/reset';
const STATS_APPLY_CALENDAR_VALUE = 'statistics/apply-calendar-value';
const STATS_UPDATE_QUERY = 'statistics/update-query';

const SHOW_PROGRESS_BAR = 'statistics/show-progress-bar';

const CHANGE_CURRENCY = 'statistics/change-currency';
const STATISTICS_ADD_BONUS_COLUMN = 'statistics/add-bonus-column';

const GET_LAST_CONVERSION_CURRENCY_SUCCESS = 'statistics/get-last-conversion-success';
const GET_LAST_CONVERSION_CURRENCY_ERROR = 'statistics/get-last-conversion-error';
const INIT_CURRENCIES = 'statistics/init-currencies';

export const APPLY_MASTER_SWITCH = 'statistics/apply-master-switch';

const initialState = {
	data: null,
	query: {
		currency: null,
		groups: ['date']
	},
	table: tableInitialState,
	calendar: calendar.initialState,
	columnControl: columnControl.initialState,
	filters: filters.initialState,
	fullAppliedFilters: null,
	groups: groups.initialState,
	listOfCurrencies: [],
	savedFilters: saveExport.initialState,
	masterSwitchConfig: [{
		id: 'master_only',
		title: 'Моя',
		active: true
	}, {
		id: 'children_only',
		title: 'Вебмастеров',
		active: false
	}, {
		id: 'all',
		title: 'Общая',
		active: false
	}],
	showProgressBar: false,
	error: false
};

export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const masterSwitchActiveTab = selectors.getMasterSwitchActiveTab(getState());
			initDefaultCurrenciesAction(getState, dispatch, INIT_CURRENCIES, 'statistics');
			const request = http({
				url: `${endpoints.STATISTICS.GET_LAST_CONVERSION}?owner_type=${masterSwitchActiveTab}`,
				method: 'get',
				actions: [
					GET_LAST_CONVERSION_CURRENCY_SUCCESS,
					GET_LAST_CONVERSION_CURRENCY_ERROR
				]
			});
			request(dispatch).then(() => fetchRequest(dispatch, getState, props));
		},

		statsApply: () => (dispatch, getState) => {
			dispatch({
				type: SHOW_PROGRESS_BAR
			});

			dispatch(actions.updateQuery());

			fetchRequest(dispatch, getState);
		},

		statsReset: () => dispatch => {
			dispatch(replace('statistics/general/'));
			dispatch({ type: STATS_RESET });
		},
		statsResetFromSavedFilter: () => dispatch => {
			dispatch(conversionsActions.statsResetFromSavedFilter());
			dispatch({ type: STATS_RESET });
		},

		sortByColumn: columnIndex => ({
			type: SORT_BY_COLUMN,
			payload: columnIndex
		}),

		sortByGroup: groupIndex => ({
			type: SORT_BY_GROUP,
			payload: groupIndex
		}),

		columnControlApply: (formData, parentColumnName) =>
			columnControl.actions.apply(formData, parentColumnName, actions.statsApply()),

		applyCalendarValue: calendarValue => ({
			type: STATS_APPLY_CALENDAR_VALUE,
			payload: calendarValue
		}),

		statisticsFilterApply: filterId => (dispatch, getState) => {
			filters.actions.filterApply(filterId)(dispatch, getState);
		},

		updateQuery: () => ({
			type: STATS_UPDATE_QUERY
		}),

		resetUnappliedFilters: () => (dispatch, getState) => {
			let currentQuery = getState().statistics.query;
			let fullAppliedFilters = getState().statistics.fullAppliedFilters;

			dispatch(
				filters.actions.filterApplyByExternalQuery({
					...currentQuery,
					filters: fullAppliedFilters
				})
			);
			dispatch(groups.actions.groupsSetValue(currentQuery && currentQuery.groups));
			currentQuery && currentQuery.date_range && dispatch(actions.applyCalendarValue(currentQuery.date_range));

		},

		showProgressBar: () => ({
			type: SHOW_PROGRESS_BAR
		}),

		changeCurrency: currencyId => dispatch => {
			dispatch({
				type: CHANGE_CURRENCY,
				payload: currencyId
			});

			dispatch(actions.statsApply());
		},

		statExport: () => (dispatch, getState) => {
			const state = getState();
			const { tableRows, groups, currency, dow } = selectors.getDataTable(state);
			const user = state.app.userData.general;
			const translator = intl(state);

			if (tableRows.length === 0 ) return;

			const headersConfig = [].concat(
				groups.map(entity => ({ id: entity.id, title: entity.id })),
				[{
					id: 'hosts',
					title: 'hosts'
				}, {
					id: 'hits',
					title: 'hits'
				}, {
					id: 'transitions',
					title: 'transitions'
				}, {
					id: 'accepted_conversions',
					title: 'approved_conversions'
				}, {
					id: 'pending_conversions',
					title: 'pending_conversions'
				}, {
					id: 'declined_conversions',
					title: 'rejected_conversions'
				}, {
					id: 'invalid_conversions',
					title: 'invalid_conversions'
				}, {
					id: 'total_conversions',
					title: 'total_conversions'
				}, {
					id: 'epc',
					title: 'epc'
				}, {
					id: 'crs',
					title: 'crs'
				}, {
					id: 'crl',
					title: 'crl'
				}, {
					id: 'approve',
					title: 'approve'
				}, {
					id: 'currency',
					title: 'currency'
				}, {
					id: 'accepted_income',
					title: 'approved_payout'
				}, {
					id: 'pending_income',
					title: 'pending_payout'
				}, {
					id: 'declined_income',
					title: 'rejected_payout'
				}]
			);

			if (user.is_master) {
				headersConfig.push({
					id: 'bonus',
					title: 'bonus'
				});
			}

			let flattenRows = [];
			// tableRows is MAP structure
			tableRows.forEach(value => {
				flattenRows = flattenRows.concat(value);
			});

			const headers = headersConfig.map(entity => entity.title);

			const columns = flattenRows.filter(entity => !entity.isTotal).map(entity => {
				return headersConfig.map(header => {
					switch (header.id) {
						case 'hour': return !(isUndefined(entity[header.id]) || isNull(entity[header.id])) ?
							`${entity[header.id]}:00 - ${++entity[header.id]}:00` : '-';
						case 'dow': return translator(dow[entity.dow]);
						case 'currency': return currency;
						default: return entity[header.id];
					}
				});
			});

			const rows = [].concat([headers], columns);

			csv(rows, (err, res) => {
				const exportFilename = `Export--Statistics--${moment().format(
					'YYYY-MM-DD--HH-mm-ss'
				)}.csv`;
				const csvData = new Blob([res], { type: 'text/csv;charset=utf-8;' });
				//IE11 & Edge
				if (navigator.msSaveBlob) {
					navigator.msSaveBlob(csvData, exportFilename);
				} else {
					//In FF link must be added to DOM to be clicked
					const link = document.createElement('a');
					link.href = window.URL.createObjectURL(csvData);
					link.setAttribute('download', exportFilename);
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				}
			});
		},

		applyMasterSwitch: id => dispatch => {
			dispatch({
				type: APPLY_MASTER_SWITCH,
				payload: id,
				meta: {
					resetFiltersAndTable: true
				}
			});
			http({
				url: `${endpoints.STATISTICS.GET_LAST_CONVERSION}?owner_type=${id}`,
				method: 'get',
				actions: [
					GET_LAST_CONVERSION_CURRENCY_SUCCESS,
					GET_LAST_CONVERSION_CURRENCY_ERROR
				]
			})(dispatch).then(() => {
				return dispatch(actions.statsApply());
			});
		}
	},
	calendar.actions,
	columnControl.actions,
	filters.actions,
	groups.actions,
	saveExport.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			const shouldSaveFiltersState =
				action.payload.pathname.indexOf('statistics/analytics') !== -1 ||
				action.payload.pathname.indexOf('statistics/general') !== -1 ||
				action.payload.pathname.indexOf('statistics/conversions') !== -1;
			return shouldSaveFiltersState ? { ...state, data: null } : initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const [data, savedFilters] = action.payload.map(res => res.data);
			return { ...state,
				data,
				savedFilters: { ...state.savedFilters, data: savedFilters },
				showProgressBar: false
			};
		},
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, data: null, error: true, showProgressBar: false }),
		[GET_LAST_CONVERSION_CURRENCY_ERROR]: (state, action) =>
			({ ...state, data: null, error: true, showProgressBar: false }),
		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns } = state.table;
			const columnIndex = action.payload;
			const direction =
				sortByColumn && sortByColumn['id'] == columnIndex
					? sortByColumn.direction === 'desc' ? 'asc' : 'desc'
					: columns[columnIndex].direction;

			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnIndex],
						direction
					}
				}
			};
		},
		[SORT_BY_GROUP]: (state, action) => {
			const { sortByGroup, groupColumns } = state.table;
			const groupIndex = action.payload;

			const queryGroups = state.query.groups;
			let direction;

			if (sortByGroup[groupIndex]) {
				direction = sortByGroup[groupIndex].direction === 'desc' ? 'asc' : 'desc';
			} else if (queryGroups.indexOf(groupIndex) !== -1) {
				direction = groupColumns[groupIndex].direction === 'desc' ? 'asc' : 'desc';
			} else {
				direction = groupColumns[groupIndex].direction;
			}

			return {
				...state,
				table: {
					...state.table,
					sortByColumn: null,
					sortByGroup: {
						...state.table.sortByGroup,
						[groupIndex]: {
							...groupColumns[groupIndex],
							direction
						}
					}
				}
			};
		},

		[STATS_APPLY_CALENDAR_VALUE]: (state, action) => {
			const dateRange = action.payload;
			const calendar = {
				...state.calendar,
				value: {
					start: (dateRange.start && moment(dateRange.start).format('YYYY-MM-DD')) || state.calendar.value.start,
					end: (dateRange.end && moment(dateRange.end).format('YYYY-MM-DD')) || state.calendar.value.end
				}
			};

			return { ...state, calendar };
		},

		[STATS_UPDATE_QUERY]: state => {
			const groups =
				state.groups && state.groups.selectControlsParams.map(control => control.value).filter(val => !!val);
			const filters = state.filters && mapQueryFilters(state.filters);
			const fullFilters = state.filters && mapQueryFilters(state.filters, false);
			const date_range = state.calendar && state.calendar.value;

			return {
				...state,
				query: {
					...state.query,
					groups: groups || state.query.groups,
					filters: filters || state.query.filters,
					date_range: date_range || state.query.date_range
				},
				fullAppliedFilters: fullFilters
			};
		},

		[STATS_RESET]: state => ({
			...initialState,
			masterSwitchConfig: state.masterSwitchConfig
		}),
		[REFETCH_ALL_DATA]: () => initialState,
		[SHOW_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),
		[CHANGE_CURRENCY]: (state, action) => ({
			...state,
			query: {
				...state.query,
				currency: action.payload
			}
		}),
		[STATISTICS_ADD_BONUS_COLUMN]: (state, action) => ({
			...state,
			table: {
				...state.table,
				columns: {
					...state.table.columns,
					bonus: {
						...state.table.columns.bonus,
						isShow: true
					}
				},
				groupColumns: {
					...state.table.groupColumns,
					affiliate: {
						id: 'affiliate',
						title: 'Вебмастер',
						direction: 'asc',
						relationToFilter: 'affiliates'
					}
				}
			}
		}),

		[APPLY_MASTER_SWITCH]: (state, action) => {
			let newState = {
				...state,
				masterSwitchConfig: state.masterSwitchConfig.map(option => {
					if (option.id === action.payload) {
						return { ...option, active: true };
					}
					return { ...option, active: false };
				})
			};

			if (action.meta && action.meta.resetFiltersAndTable) {
				newState = {
					...newState,
					table: tableInitialState,
					filters: filters.initialState,
					query: initialState.query,
					groups: groups.initialState
				};
			}

			return newState;
		},
		[GET_LAST_CONVERSION_CURRENCY_SUCCESS]: (state, action) => isRocket ? state : toggleInitialCurrency(state, action),
		[INIT_CURRENCIES]: (state, action) => initCurrencies(state, action)
	},
	calendar.reducers,
	columnControl.reducers,
	filters.reducers,
	groups.reducers,
	saveExport.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

export const selectors = {
	getData: state => state.statistics.data,
	getDataTable: state => {
		let groups = state.statistics.query.groups;
		let translator = intl(state);

		groups = groups.map(group => {
			const { groupColumns, sortByGroup } = state.statistics.table;
			const item = groupColumns[group];
			const direction =
				sortByGroup && sortByGroup[group] ? sortByGroup[group].direction : groupColumns[group].direction;
			return { ...item, title: translator(item.title), direction };
		});

		const parentColumn = columns => (name, isShowedAutoTrash) => ({
			shouldShow: !!values(columns)
				.filter(c => c.parent === name && (c.isShow || isShowedAutoTrash && c.isOnFlag)).length,
			colSpan: values(columns)
				.filter(c => c.parent === name && (c.isShow || isShowedAutoTrash && c.isOnFlag)).length
		});

		const childColumn = columns => (columnName, className = 'last-element-in-parent') => {
			const currentColumn = columns[columnName];
			const columnParent = currentColumn.parent;
			const allVisibleColumnInParent = values(columns).filter(
				column => column.parent === columnParent && column.isShow
			);
			const lastColumn = last(allVisibleColumnInParent);
			const shouldAddClassName = lastColumn && lastColumn.id === currentColumn.id;

			return Object.assign(
				{
					shouldShow: currentColumn.isShow
				},
				shouldAddClassName
					? {
						className
					}
					: {}
			);
		};

		const language = state.app.userData && state.app.userData.general && state.app.userData.general.language;
		const dow =
			!language || language === 'en'
				? ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота']
				: ['понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота', 'воскресенье'];

		const tableRows =
			state.statistics.data &&
			transformRows(
				state.statistics.data,
				groups,
				state.statistics.table.sortByColumn,
				state.statistics.table.sortByGroup,
				state.statistics.columnControl,
				language
			);

		return {
			groups,
			tableRows,
			columnsConfig: state.statistics.table.columns,
			shouldShowGroupHeader: groups && groups.length > 1,
			parentColumn: parentColumn(state.statistics.table.columns),
			childColumn: childColumn(state.statistics.table.columns),
			currentSorting: state.statistics.table.sortByColumn && state.statistics.table.sortByColumn.id,
			currency: state.app.userData && state.app.userData.activeBalance,
			dataForGoalsQuery: {
				queryContext: getRequestQuery(
					state.statistics,
					getCurrentCurrency(state, 'statistics'),
					null,
					true,
					selectors.getIsMasterStatus(state),
					state.statistics.masterSwitchConfig
				),
				groupColumns: state.statistics.table.groupColumns,
				conversionStatuses:
					state.app.lists &&
					state.app.lists['conversion-statuses'] &&
					state.app.lists['conversion-statuses'].map(status => ({ id: status.id, text: capitalize(status.text) }))
			},
			dow
		};
	},
	getCalendar: state => calendar.selectors.getCalendar(state => state.statistics.calendar, state => state)(state),
	getColumnControlConfig: state =>
		columnControl.selectors.getConfig(state => state.statistics.columnControl, state => state)(state),
	getFilters: state => filters.selectors.selectFilters(state => state.statistics.filters, state => state)(state),
	getAppliedFilters: state => filters.selectors.appliedFilters(state),
	getGroups: state => groups.selectors.getData(state => state.statistics.groups, state => state)(state),
	getSavedFilters: state =>
		saveExport.selectors.getSavedFilters(state => state.statistics.savedFilters, state => state)(state),
	getIntl: intl,
	getProgressBar: state => state.statistics.showProgressBar,
	getListOfCurrencies: state => state.app.userData.balances.map(item => ({
		id: item.currency,
		text: item.currency
	})),
	getActiveCurrency: state => getCurrentCurrency(state, 'statistics'),
	getMasterSwitchConfig: state => state.statistics.masterSwitchConfig,
	getIsShowedAutoTrash: state => _get(state, 'app.userData.general.feature_flags.autotrash', false),
	getMasterSwitchActiveTab: state => state.statistics.masterSwitchConfig.filter(i => i.active)[0].id,
	getIsMasterStatus: state => state.app.userData.general.is_master,
	getError: state => state.statistics.error
};

function  fetchRequest(dispatch, getState, props) {
	const state = getState();
	const user = state.app.userData.general;
	const activeBalance = getCurrentCurrency(state, 'statistics');
	const externalQuery = (props && props.location && props.location.query && qs.parse(props.location.query)) || {};
	const query = qs.stringify(
		getRequestQuery(
			state.statistics,
			activeBalance,
			externalQuery,
			false,
			user.is_master,
			state.statistics.masterSwitchConfig,
		),
		{ arrayFormat: 'brackets' }
	);
	const activeMasterSwitchSetting = find(state.statistics.masterSwitchConfig, { active: true }).id;

	const request = http({
		url: [`${endpoints.STATISTICS.GENERAL}?${query}`, `${endpoints.PROFILE.SAVED_FILTERS}`],
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR],
		addToResponse: externalQuery
	});

	return request(dispatch).then(action => {
		if (action.type === FETCH_DATA_SUCCESS) {
			if (
				user.is_master &&
				(activeMasterSwitchSetting !== 'master_only' ||
					(!isEmpty(externalQuery) && externalQuery.filters && externalQuery.filters.affiliates))
			) {
				dispatch(
					filters.actions.filterReplaceState(
						[].concat(filterInitialState, {
							title: 'Вебмастера',
							suggest: [
								{
									placeholder: 'Выберите вебмастера',
									key: 'affiliates',
									options: {
										source: endpoints.STATISTICS.FILTERS.AFFILIATES,
										type: 'fromServer'
									}
								}
							]
						})
					)
				);

				dispatch({
					type: STATISTICS_ADD_BONUS_COLUMN
				});
			}

			if (!isEmpty(externalQuery)) {
				dispatch(filters.actions.filterApplyByExternalQuery(externalQuery));
				dispatch(columnControl.actions.columnControlApplyByExternalQuery(externalQuery));
				if (!isEmpty(externalQuery.groups)) {
					dispatch(groups.actions.groupsSetValue(externalQuery.groups));
				}
				if (!isEmpty(externalQuery.date_range)) {
					dispatch(actions.applyCalendarValue(externalQuery.date_range));
				}
				if (externalQuery.filters && externalQuery.filters.affiliates) {
					dispatch({ type: APPLY_MASTER_SWITCH, payload: 'children_only', meta: { resetFiltersAndTable: false } });
				}
				dispatch(actions.updateQuery());
			} else {
				dispatch(actions.resetUnappliedFilters());
			}
		}
		return action;
	});
}



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/statistics/general/index.js